'use client'

import { useStore } from 'jotai'
import { Maximize2, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'

import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { wizardPriceStore } from '@/features/dashboard/lib/wizard-price-store'
import { instrumentKindTag, instrumentRank } from '@/features/hedge/lib/instrument-display'
import { ORDER_FORM_FIELD_CLASS } from '@/features/order/order-form/components/widget/order-form-fields'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { watchListTickerMapAtom } from '@/state/dashboard'
import { Exchange, Instrument } from '@/types/generated/common/types'

const WatchlistWizard = dynamic(
  () =>
    import('@/features/dashboard/components/watchlist-wizard/watchlist-wizard').then(
      (module) => module.WatchlistWizard
    ),
  { ssr: false }
)

const MAX_RENDERED = 50

interface RfqInstrumentSelectProps {
  value: string
  displayValue?: string
  exchange: Exchange
  instruments: Instrument[]
  onChange: (instrument: Instrument, lastPrice?: string) => void
}

/**
 * The compact selector handles quick exchange-scoped selection. Its footer opens the full
 * WatchlistWizard when the user needs scalable asset/contract navigation.
 */
export function RfqInstrumentSelect({
  value,
  displayValue,
  exchange,
  instruments,
  onChange,
}: RfqInstrumentSelectProps) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const t = useTranslations('orderForm')
  // Read-only-at-selection-time, not a render subscription: watchListTickerMapAtom is a
  // single global map coalesced-written on every WS ticker tick (~100ms), and this
  // component (mounted for every RFQ leg row) has no price in its own JSX — only
  // currentPrice(), called from choose() at click time, ever reads it. useAtomValue here
  // would re-render every instance on every tick regardless of whether the popover is
  // even open, for a value the render never uses (the whole-map-subscribe anti-pattern
  // documented on watchListTickerMapAtom's own declaration — see watch-list.tsx's
  // click-time store.get() for the same pattern).
  const store = useStore()
  const [selectOpen, setSelectOpen] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [query, setQuery] = useState('')
  const exchangeIconKey = getExchangeIconKey(exchange)

  const sorted = useMemo(
    () =>
      instruments
        .filter((instrument) => instrument.isActive)
        .toSorted(
          (a, b) => instrumentRank(a) - instrumentRank(b) || a.symbol.localeCompare(b.symbol)
        ),
    [instruments]
  )

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return sorted
    return sorted.filter(
      (instrument) =>
        instrument.symbol.toLowerCase().includes(normalizedQuery) ||
        instrument.exchangeSymbol.toLowerCase().includes(normalizedQuery)
    )
  }, [query, sorted])

  const visible = useMemo(() => filtered.slice(0, MAX_RENDERED), [filtered])
  const hiddenCount = filtered.length - visible.length

  const currentPrice = (instrument: Instrument) => {
    const key = watchlistEntryKey(instrument.exchange, instrument.symbol)
    const cachedTicker = store.get(watchListTickerMapAtom)[key]
    return (
      cachedTicker?.lastPrice ??
      wizardPriceStore.getSnapshot()[key]?.price ??
      cachedTicker?.markPrice ??
      cachedTicker?.indexPrice
    )
  }

  const choose = (instrument: Instrument, lastPrice = currentPrice(instrument)) => {
    onChange(instrument, lastPrice)
    setQuery('')
    setSelectOpen(false)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (event.nativeEvent.isComposing) return
    if (event.key === 'Enter') {
      event.preventDefault()
      const selected = visible[0]
      if (selected) choose(selected)
    }
  }

  const openWizard = () => {
    setSelectOpen(false)
    setQuery('')
    setWizardOpen(true)
  }

  return (
    <>
      <Select
        open={selectOpen}
        value={value || undefined}
        onValueChange={(symbol) => {
          const instrument = sorted.find((candidate) => candidate.symbol === symbol)
          if (instrument) choose(instrument)
        }}
        onOpenChange={(next) => {
          if (!next) setQuery('')
          setSelectOpen(next)
        }}
      >
        <SelectTrigger className={cn(ORDER_FORM_FIELD_CLASS, 'w-full text-left')}>
          <span className="flex min-w-0 items-center gap-2">
            {exchangeIconKey ? (
              <ExchangeIcon exchange={exchangeIconKey} className="size-4 shrink-0 rounded-full" />
            ) : null}
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                !value && 'text-muted-foreground font-normal'
              )}
            >
              {formatSymbol(displayValue || value) || t('selectInstrument')}
            </span>
          </span>
        </SelectTrigger>
        <SelectContent
          align="start"
          // Trigger lives inside the order-form popup's scrollable body — the
          // default 'optimized' strategy only repositions on tracked
          // scroll/resize events, which was leaving the dropdown anchored to
          // a stale spot when opened after scrolling that container. 'always'
          // repositions every animation frame while open instead.
          updatePositionStrategy="always"
          className="z-[60]"
          viewportClassName="h-auto! max-h-72 gap-0! p-0"
          footer={
            <button
              type="button"
              onPointerDown={(event) => event.preventDefault()}
              onClick={openWizard}
              className="text-foreground/70 hover:bg-foreground/5 hover:text-foreground flex w-full items-center gap-2 border-t px-3 text-left text-xs transition-colors"
            >
              <Maximize2 className="size-3.5" aria-hidden="true" />
              {t('rfqBrowseAllInstruments')}
            </button>
          }
        >
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
            <Search className="text-foreground/50 size-3.5 shrink-0" aria-hidden="true" />
            <input
              autoFocus={canAutoFocusSearch}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t('searchInstruments')}
              className="placeholder:text-foreground/40 min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>
          <div className="p-1">
            {visible.length === 0 ? (
              <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                {sorted.length === 0 ? t('noInstrumentsLoaded') : t('noMatches')}
              </p>
            ) : (
              <>
                {visible.map((instrument) => (
                  <SelectItem
                    key={instrument.internalId}
                    value={instrument.symbol}
                    showCheck
                    className="pr-8"
                  >
                    <span className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        {formatSymbol(instrument.exchangeSymbol || instrument.symbol)}
                      </span>
                      <span className="text-muted-foreground shrink-0 text-[10px]">
                        {instrumentKindTag(instrument)}
                      </span>
                    </span>
                  </SelectItem>
                ))}
                {hiddenCount > 0 ? (
                  <p className="text-muted-foreground px-2 py-1.5 text-center text-[10px]">
                    {t('rfqMoreInstrumentsHint', { count: hiddenCount })}
                  </p>
                ) : null}
              </>
            )}
          </div>
        </SelectContent>
      </Select>

      <WatchlistWizard
        mode="single"
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        lockedExchangeFilter={exchange}
        detailSelectionOnly
        onSelect={(instrument, lastPrice) => choose(instrument, lastPrice)}
      />
    </>
  )
}
